import type { FailureCode } from "../drawer-module";

/**
 * The manual-open acknowledgement (T138, FR-103).
 *
 * A FAILURE IS NEVER SILENT. When the drawer does not open electronically,
 * someone still opens it — with a key, or by the release lever underneath. That
 * open is a real cash event, and if nothing records it there is a gap in the
 * audit trail at exactly the moment the controls are weakest. Silence is what a
 * thief relies on.
 *
 * So this dialog is not an error message. It is a RECORD BEING TAKEN, and its
 * wording says so: acknowledging produces a `manual/success` row alongside the
 * `electronic/failed` one, and the pair reads as "the hardware was down and
 * someone opened it by hand at 14:22".
 *
 * IT CANNOT BLOCK THE SALE (FR-102). The money is already taken and the receipt
 * already printed by the time this appears. Dismissing it is allowed — the
 * failure row stands on its own, and refusing to let staff continue would only
 * teach them to avoid the feature.
 */

const EXPLANATIONS: Record<FailureCode, { title: string; detail: string }> = {
  unsupported_browser: {
    title: "This device cannot open the drawer",
    detail:
      "Drawer control is not available in this browser, so the drawer has to be opened by hand.",
  },
  permission_lost: {
    title: "The drawer is no longer set up",
    detail:
      "Permission to use the drawer was lost. Open it by hand now, then reconnect it in settings.",
  },
  device_disconnected: {
    title: "The drawer did not respond",
    detail:
      "It may be unplugged or switched off. Open it by hand now, and check the cable when there is a moment.",
  },
  kick_timeout: {
    title: "The drawer did not open",
    detail:
      "The command was sent but nothing happened. Open it by hand now, and check the drawer when there is a moment.",
  },
};

export interface ManualOpenDialogProps {
  failureCode: FailureCode;
  /** Called when staff confirm they opened it. Writes the manual/success row. */
  onAcknowledge: () => void;
  /** Called when they did not. The failure row still stands. */
  onDismiss: () => void;
}

export function ManualOpenDialog({
  failureCode,
  onAcknowledge,
  onDismiss,
}: ManualOpenDialogProps) {
  const explanation = EXPLANATIONS[failureCode];

  return (
    <div className="manualopen" role="alertdialog" aria-label="The drawer did not open">
      <div className="manualopen__panel">
        <h2 className="manualopen__title">{explanation.title}</h2>
        <p className="manualopen__detail">{explanation.detail}</p>

        {/* Said plainly, because the alternative is staff believing a failed
            drawer failed the sale and re-ringing it. */}
        <p className="manualopen__reassure">
          The sale is complete and has been recorded. Only the drawer is affected.
        </p>

        <div className="manualopen__actions">
          <button type="button" className="manualopen__dismiss" onClick={onDismiss}>
            Not now
          </button>
          <button type="button" className="is-primary" onClick={onAcknowledge}>
            I opened it by hand
          </button>
        </div>

        <p className="manualopen__note">
          Confirming records who opened the drawer and when, so the count at the end of the
          shift still adds up.
        </p>
      </div>
    </div>
  );
}

export default ManualOpenDialog;
